/**
 * /help — an illustrated guide to the dashboard, reached from the user menu's Help item.
 *
 * ⚠ A FOURTH PAGE SHAPE, on purpose (spec/03 says not to invent one without saying why). The
 * archetypes — list (A), record (B), queue-plus-document (C) — are data surfaces, and every rule
 * they carry (one section mounted, hydrate on demand, cache once, summary counts) exists to bound
 * requests and data-sized DOM. This page makes ZERO requests, its size is fixed at authoring time,
 * and it is read top to bottom or entered by a deep link — the one use that tabs and one-at-a-time
 * mounting make worse. So it is a content page: one scrolling column of `dp-card`s with a sticky
 * anchor rail, in archetype-B chrome so it reads as the same product. The cost the one-section
 * rule guards against is paid here as ~17 CSS animations, bounded by PAUSING whatever is off-screen
 * (`useInView`) rather than by unmounting it.
 *
 * Sections are gated by the same allowlist as the sidebar (`canOpen`), so nobody reads about a
 * page they cannot open. The support request that used to fire straight from the menu lives in the
 * card at the bottom, behind the same confirm (`users/support/` Telegram-messages every support
 * account). No mockup exists for this screen; `profile.html` is the nearest reference.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Anchor, Button, Modal } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import Alerts from "/src/util/alert";

import { HELP_PAGE, MANAGER_PROFILE, PROFILE_PAGE, SIDE_NAV } from "../../config/lang";
import { canOpen } from "../../layout/AppNav";
import { call } from "../../util/action";
import { translate } from "../../util/util";
import * as I from "./illustrations";
import "./help.scss";

/** `path` is the MENU row that gates the section; shell sections have none and show for every role.
 *  `nav` is that row's SIDE_NAV key: a section that IS one nav row takes the sidebar's own label as its
 *  title, so the guide and the menu can never call the same page two different things. */
const SECTIONS = [
  { id: "search", bullets: 4, Illo: I.SearchIllo },
  { id: "notifications", bullets: 3, Illo: I.NotificationsIllo },
  { id: "account", bullets: 4, Illo: I.AccountIllo },
  { id: "lists", bullets: 6, Illo: I.ListsIllo },
  { id: "overview", path: "/overview", nav: "overview", bullets: 4, Illo: I.OverviewIllo },
  { id: "photo_review", path: "/photo-review", nav: "photo_report", bullets: 5, Illo: I.PhotoReviewIllo },
  { id: "visits", path: "/visit", nav: "visit", bullets: 4, Illo: I.VisitsIllo },
  { id: "tracking", path: "/tracking", nav: "tracking", bullets: 4, Illo: I.TrackingIllo },
  { id: "bi_reports", path: "/bi-report", nav: "all_reports", bullets: 3, Illo: I.BiReportsIllo },
  { id: "tasks", path: "/task", nav: "tasks", bullets: 4, Illo: I.TasksIllo },
  { id: "answers", path: "/answers", nav: "answers", bullets: 4, Illo: I.AnswersIllo },
  { id: "import", path: "/import", bullets: 4, Illo: I.ImportIllo },
  { id: "merchandisers", path: "/merchandiser", nav: "merchandiser", bullets: 4, Illo: I.MerchandisersIllo },
  { id: "supervisors", path: "/supervisors", nav: "supervisors", bullets: 3, Illo: I.SupervisorsIllo },
  { id: "documents", path: "/document", nav: "documents", bullets: 3, Illo: I.DocumentsIllo },
  { id: "surveys", path: "/surveys", nav: "pt_surveys", bullets: 4, Illo: I.SurveysIllo },
  { id: "reference", path: "/points", bullets: 4, Illo: I.ReferenceIllo },
];

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

/** Decorative: the copy beside it says what it shows, and nothing inside is focusable. */
function Illo({ children }) {
  const ref = useRef(null);
  const play = useInView(ref);
  return (
    <div ref={ref} className={`hp-illo${play ? " hp-illo--play" : ""}`} aria-hidden="true">
      {children}
    </div>
  );
}

function SupportCard({ t, lang }) {
  /* A static `Modal.confirm` does not inherit ConfigProvider's locale, so the buttons are named
     explicitly. `onOk` returns the promise so OK shows its loading state until the call answers. */
  const ask = () =>
    Modal.confirm({
      title: translate(MANAGER_PROFILE, "my_help", lang),
      icon: <QuestionCircleOutlined />,
      content: translate(PROFILE_PAGE, "help_confirm", lang),
      okText: translate(PROFILE_PAGE, "help_send", lang),
      cancelText: translate(PROFILE_PAGE, "discard", lang),
      onOk: () =>
        new Promise(resolve => {
          call({ method: "GET", url: "users/support/" }, r => {
            /* The endpoint answers 200 `{ ok, delivered }`, and `ok` is false when NO support account could
               be reached (a blocked bot, a stale chat id) — so the body's flag is the truth, not the HTTP
               status. `isOk()` would call an undelivered request a success. */
            if (r?.ok) Alerts.success(translate(PROFILE_PAGE, "help_sent", lang));
            else Alerts.warning(translate(PROFILE_PAGE, "help_failed", lang));
            resolve();
          });
        }),
    });
  return (
    <section id="support" className="dp-card hp-section hp-support">
      <h2 className="hp-sec-title">{t("support_title")}</h2>
      <p className="hp-desc">{t("support_desc")}</p>
      <Button type="primary" icon={<QuestionCircleOutlined />} onClick={ask}>
        {t("support_btn")}
      </Button>
    </section>
  );
}

export default function Help() {
  const me = useSelector(state => state.me);
  const lang = me?.language;
  const t = key => translate(HELP_PAGE, key, lang);
  const titleOf = s => (s.nav ? translate(SIDE_NAV, s.nav, lang) : t(`${s.id}_title`));
  const { hash } = useLocation();
  const sections = SECTIONS.filter(s => !s.path || canOpen(s.path, me?.role));

  /* `--header-h` is a fixed 64px token (tokens.css; `.shell-bar` never wraps), read once the way
     overview's useTokens() reads its palette. `.hp-section { scroll-margin-top }` is the same sum. */
  const offset = useMemo(() => {
    const h = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-h"));
    return (Number.isFinite(h) ? h : 64) + 16;
  }, []);

  /* antd's Anchor picks the active link on mount but never scrolls to the hash — the browser tried
     before React rendered the sections. `scroll-margin-top` keeps the landing clear of the bar. */
  useEffect(() => {
    const el = hash && document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView();
  }, [hash]);

  const items = [...sections.map(s => ({ key: s.id, href: `#${s.id}`, title: titleOf(s) })), { key: "support", href: "#support", title: t("support_title") }];

  return (
    <div className="shell-content hp">
      <div className="dp-head">
        <div className="dp-head__main">
          <h1 className="dp-title">{t("title")}</h1>
          <p className="hp-sub">{t("subtitle")}</p>
        </div>
      </div>
      <div className="hp-grid">
        <nav className="hp-rail" aria-label={t("rail_label")}>
          {/* `affix={false}`: the rail is stuck with CSS, in its grid column. antd then adds
              `.ant-anchor-fixed`, which hides the ink unless `showInkInFixed`.
              ponytail: antd's click scroll is a 450ms JS tween that ignores reduced motion, and
              `replace` writes history that react-router does not observe — harmless, nothing reads
              the hash after mount. Upgrade: onClick → preventDefault + scrollIntoView + history.replace. */}
          <Anchor affix={false} showInkInFixed replace targetOffset={offset} items={items} getCurrentAnchor={active => active || items[0].href} />
        </nav>
        <div className="hp-body">
          {sections.map(section => {
            const { id, bullets, Illo: Picture } = section;
            return (
              <section key={id} id={id} className="dp-card hp-section">
                <h2 className="hp-sec-title">{titleOf(section)}</h2>
                <p className="hp-desc">{t(`${id}_desc`)}</p>
                <ul className="hp-list">
                  {Array.from({ length: bullets }, (_, i) => (
                    <li key={i}>{t(`${id}_b${i + 1}`)}</li>
                  ))}
                </ul>
                <Illo>
                  <Picture />
                </Illo>
              </section>
            );
          })}
          <SupportCard t={t} lang={lang} />
        </div>
      </div>
    </div>
  );
}
